'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, ButtonProps } from '@udemy-v2/react-core-components';
import { useI18n, LocalizedHtml } from '@udemy-v2/i18n';
import { publishEvent } from '@udemy-v2/event-tracking';
import { BuyNowEvent } from '@/features/course-landing-page/events/events';
import { PaymentMethodImage } from '@/components/payment-method-image';
import { OneClickCheckoutContext } from '@/types/one-click-checkout-context';
import { useCallback } from 'react';
import styles from './one-click-checkout-button.module.scss';

/**
 * Props for OneClickCheckoutButton component
 */
export interface OneClickCheckoutButtonProps extends ButtonProps {
  /**
   * ID of the course for checkout
   */
  courseId: number;
  /**
   * Tracking ID for analytics
   */
  courseTrackingId?: string;
  /**
   * One-click checkout data
   */
  oneClickCheckoutContext: OneClickCheckoutContext;
}

/**
 * OneClickCheckoutButton component displays a button that allows users
 * to proceed directly to checkout with their saved payment method.
 *
 * The button automatically fetches and displays the user's saved payment
 * information and only renders when one-click checkout is available.
 *
 * @param props - Component props
 * @returns One-click checkout button or null if not enabled
 */
export function OneClickCheckoutButton(t0) {
  const $ = _c(29);
  let buttonProps;
  let courseId;
  let courseTrackingId;
  let onClick;
  let oneClickCheckoutContext;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      courseId,
      courseTrackingId,
      size: t1,
      udStyle: t2,
      onClick,
      oneClickCheckoutContext,
      ...buttonProps
    } = t0);
    $[0] = t0;
    $[1] = buttonProps;
    $[2] = courseId;
    $[3] = courseTrackingId;
    $[4] = onClick;
    $[5] = oneClickCheckoutContext;
    $[6] = t1;
    $[7] = t2;
  } else {
    buttonProps = $[1];
    courseId = $[2];
    courseTrackingId = $[3];
    onClick = $[4];
    oneClickCheckoutContext = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  const size = t1 === undefined ? "large" : t1;
  const udStyle = t2 === undefined ? "secondary" : t2;
  const {
    gettext
  } = useI18n();
  const {
    isEnabled,
    cardBrand,
    cardLast4Digit,
    checkoutUrl
  } = oneClickCheckoutContext;
  let t3;
  if ($[8] !== checkoutUrl || $[9] !== courseId || $[10] !== courseTrackingId || $[11] !== onClick) {
    t3 = e => {
      if (courseTrackingId) {
        publishEvent(new BuyNowEvent({
          buyable: {
            id: courseId,
            type: "course",
            trackingId: courseTrackingId
          }
        }));
      }
      onClick?.(e);
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    };
    $[8] = checkoutUrl;
    $[9] = courseId;
    $[10] = courseTrackingId;
    $[11] = onClick;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleClick = t3;
  if (!isEnabled) {
    return null;
  }
  let t4;
  if ($[13] !== gettext) {
    t4 = gettext("Proceed with <a class=\"cardBrand\"></a> <a class=\"cardLast4Digit\"></a>");
    $[13] = gettext;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== cardBrand) {
    t5 = <PaymentMethodImage methodType={cardBrand} className={styles["brand-image"]} />;
    $[15] = cardBrand;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const t6 = `**** ${cardLast4Digit}`;
  let t7;
  if ($[17] !== t5 || $[18] !== t6) {
    t7 = {
      cardBrand: t5,
      cardLast4Digit: t6
    };
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t4 || $[21] !== t7) {
    t8 = <LocalizedHtml html={t4} interpolate={t7} />;
    $[20] = t4;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== buttonProps || $[24] !== handleClick || $[25] !== size || $[26] !== t8 || $[27] !== udStyle) {
    t9 = <Button data-purpose="one-click-checkout-button" udStyle={udStyle} className={styles["one-click-checkout-button"]} onClick={handleClick} size={size} {...buttonProps}>{t8}</Button>;
    $[23] = buttonProps;
    $[24] = handleClick;
    $[25] = size;
    $[26] = t8;
    $[27] = udStyle;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
}